import { Upload, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';

import { PictoContrats } from '@/assets/icons-april-fr/pictos';
import AprilPicto from '@/components/shared/april-picto';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import useBrandName from '@/hooks/use-brand-name';
import { formatDate } from '@/lib/dates';
import type { ContractDocument } from '@/types/api/document';

export interface DocumentsProps {
    documents: ContractDocument[];
    uploadAppLink?: string;
    isContractApporteur43397: boolean;
}

const Documents = ({ documents, uploadAppLink, isContractApporteur43397 }: DocumentsProps) => {
    const brandName = useBrandName();
    /* Premier document de chaque pièce, dans l'ordre d'origine */
    const uniqueAdhesions = documents.filter((doc, index) => documents.findIndex((d) => d.nb_piece === doc.nb_piece) === index);
    const [selectedAdhesion, setSelectedAdhesion] = useState('');
    const filteredDocuments = selectedAdhesion ? documents.filter(doc => doc.nb_piece === selectedAdhesion) : [];

    useEffect(() => {
        if (uniqueAdhesions.length > 0) {
            const recentPiece = Math.max(...uniqueAdhesions.map(adhesion => parseInt(adhesion.nb_piece ?? '')));
            setSelectedAdhesion(recentPiece.toString());
        }
    }, []);

    const getDocBadge = (d: ContractDocument) => {
        if (!d.recu) {
            return <Badge variant="destructive" className="text-[10px] sm:text-xs">Non reçu</Badge>;
        }
        if (d.traite) {
            return (
                <Badge variant="success" className="text-[10px] sm:text-xs text-center">
                    <span className="sm:hidden">Validé</span>
                    <span className="hidden sm:inline">Validé {d.dateTraitement && `le ${formatDate(d.dateTraitement)}`}</span>
                </Badge>
            );
        }
        return <Badge variant="warning" className="text-[10px] sm:text-xs text-center">
            <span className="sm:hidden">En cours</span>
            <span className="hidden sm:inline">En cours de traitement</span>
        </Badge>;
    };

    return (
        <Card>
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <AprilPicto icon={PictoContrats} size="md" />
                    Mes documents
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                {filteredDocuments.length > 0 ? (
                    <>
                        {filteredDocuments.map((d) => (
                            <div key={d.code}>
                                <div className="relative p-2 bg-muted/50 rounded-lg mb-4 transition-all duration-200 hover:bg-primary/10 hover:translate-x-1">
                                    <div className="absolute top-2 right-2">
                                        {getDocBadge(d)}
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3 pr-20 sm:pr-28">
                                        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 shrink-0">
                                            <AprilPicto icon={PictoContrats} size="sm" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-sm sm:text-base truncate">{d.libelle}</p>
                                            {d.commentaire && (
                                                <p className="text-xs sm:text-sm text-muted-foreground truncate">{d.commentaire}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {isContractApporteur43397 && d.code === 'B' && d.recu && (
                                    <div className="mt-2 mb-4 ml-4 p-6 border-l-4 border-primary bg-primary/5 rounded-r-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                        <div className="flex-1">
                                            <p className="font-semibold text-sm mb-1">Simplifiez l'immatriculation de votre nouveau véhicule</p>
                                            <p className="text-sm text-muted-foreground">
                                                Avec Certimat, service partenaire d'{brandName} et sécurisé, effectuez cette démarche simplement, rapidement et sans vous déplacer !
                                            </p>
                                        </div>
                                        <Button asChild className="text-white hover:border-none transition-all duration-300 ease-in-out cursor-pointer shrink-0">
                                            <a href="https://certimat.fr/prescripteurs?partner=3871" target="_blank" rel="noopener noreferrer">
                                                <ExternalLink className="w-4 h-4" />
                                                Je fais ma carte grise
                                            </a>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                ) : (
                    <p className="text-sm text-muted-foreground">Pas de documents pour cette adhésion.</p>
                )}

                {documents.some((doc) => doc.recu === false) && (
                    <div className="text-center mt-6">
                        <Button asChild variant="outline" className="cursor-pointer">
                            <a href={uploadAppLink} target="_blank" rel="noopener noreferrer">
                                <Upload className="w-4 h-4" />
                                J'envoie mes documents
                            </a>
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default Documents;
